import { h, Fragment } from 'preact';
import { useState, useMemo, useRef, useEffect } from 'preact/hooks';
import { getColourVariable, normalizeShopifyProductGid, parseRelatedColourwaysMetafield, } from '../../utils/relatedColourways';
import { usePromoMessageMap } from '../../search/usePromoMessageMap';
import { QuickShopButton } from './QuickShopButton';
import { QuickShopModal } from './QuickShopModal';
import { ProductCardCarousel } from './ProductCardCarousel';
import { useAnalytics } from '../../analytics/useAnalytics';
import { useTrackingContext } from '../../analytics/context';
import { getShopifyImageSrcSet, getShopifyImageUrl } from '../../utils/shopifyImageUrl';
import styles from './ProductCard.module.scss';
const CARD_IMAGE_WIDTHS = [320, 375, 480, 680, 800];
const CARD_IMAGE_SIZES = '(max-width: 767px) 50vw, (max-width: 1199px) 33vw, 25vw';
// AQ-1087: matches the repo's existing mobile/desktop convention — see
// `DESKTOP_MIN_WIDTH` in useColourwayStorefrontData.ts and `mobileBreakpointPx`
// in mini-cart-widget-app.tsx (both split at 768/769px).
const MOBILE_MAX_WIDTH = 768;
const toImageUrl = (img) => {
    if (!img)
        return undefined;
    if (typeof img === 'string')
        return img;
    return img.url;
};
const isVideoUrl = (url) => !!url && /\.(mp4|webm|mov)(\?|$)/i.test(url);
// AQ-1087: builds one carousel slide from a raw product image/video. Mirrors
// the split ProductCard already uses for the desktop hover-swap: a video's
// `src` is left untouched (Shopify's `width` CDN param is image-only), while
// an image gets the same responsive src/srcSet as the primary + hover images.
const toMediaItem = (img, fallbackAlt) => {
    var _a;
    const url = toImageUrl(img);
    if (!url)
        return undefined;
    const isObj = !!img && typeof img !== 'string';
    const isVideo = isVideoUrl(url);
    return {
        url: isVideo ? url : ((_a = getShopifyImageUrl(url, 480)) !== null && _a !== void 0 ? _a : url),
        alt: (isObj && img.alt) || fallbackAlt,
        isVideo,
        poster: isObj ? img.video_thumbnail : undefined,
        srcSet: isVideo ? undefined : getShopifyImageSrcSet(url, CARD_IMAGE_WIDTHS),
        sizes: isVideo ? undefined : CARD_IMAGE_SIZES,
    };
};
// Tag-driven promo messaging (AQS-46). Products tagged `promo:<handle>` look up the
// matching `plp_promo_message` metaobject fetched via the Storefront API on mount.
// The first tag matching `promo:*` with a resolvable handle wins.
function extractPromoHandle(tags) {
    if (!tags)
        return null;
    for (const tag of tags) {
        const match = /^promo:(.+)$/i.exec(tag);
        if (match)
            return match[1].toLowerCase().trim();
    }
    return null;
}
// Helper to extract color from variant options
const getVariantColor = (variant) => {
    if (!variant.merged_options)
        return null;
    for (const option of variant.merged_options) {
        const [key, value] = option.split(':');
        const normalisedKey = (key || '').toLowerCase();
        if (normalisedKey === 'colour' || normalisedKey === 'color') {
            return typeof value === 'string' ? value : null;
        }
    }
    return null;
};
// Helper to get color hex value from color name
const getColorHex = (colorName) => {
    const colorMap = {
        'black': '#1a1a1a',
        'brown': '#6B4226',
        'blue': '#0000FF',
        'beige': '#F5F5DC',
        'burgundy': '#7B1F2E',
        'cognac': '#9A4C1E',
        'dark brown': '#654321',
        'green': '#008000',
        'grey': '#B0B0B0',
        'gray': '#B0B0B0',
        'maroon': '#800020',
        'multi': '#9E9E9E',
        'navy': '#1B2A4A',
        'khaki': '#F0E68C',
        'orange': '#FFA500',
        'olive': '#4A5A3A',
        'odin white sneaker': '#F5F0E8',
        'pink': '#FFC0CB',
        'purple': '#800080',
        'red': '#FF0000',
        'stone': '#C9B99B',
        'tan': '#C4956A',
        'teal': '#008080',
        'yellow': '#FFFF00',
        'white': '#F5F0E8',
    };
    const normalized = (colorName || '').toLowerCase().trim();
    return colorMap[normalized] || '#CCCCCC'; // Default grey if color not found
};
/** Format a numeric string as `$amount` (matches the rest of the card — not `A$` / locale prefix from AUD). */
function formatAmount(amount) {
    const n = Number.parseFloat(amount);
    if (!Number.isNaN(n)) {
        const numStr = new Intl.NumberFormat(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(n);
        return `$${numStr}`;
    }
    return `$${amount}`;
}
/** Resolve sale state + formatted prices for an active colourway. */
function formatColourwayPrice(colourway) {
    const price = Number.parseFloat(colourway.priceAmount);
    const compare = Number.parseFloat(colourway.compareAtPriceAmount);
    const onSale = Number.isFinite(price) && Number.isFinite(compare) && compare > price;
    return {
        onSale,
        discount: onSale ? Math.round(((compare - price) / compare) * 100) : 0,
        sale: formatAmount(colourway.priceAmount),
        regular: formatAmount(colourway.compareAtPriceAmount),
    };
}
export function ProductCard({ product, layout = 'gridFour', imagePriority = false, colourwayStorefrontData, }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x;
    const { title, price, image, images, availability, productType, customAttributes, metafields } = product;
    const analytics = useAnalytics();
    const tracking = useTrackingContext();
    const { map: promoMessageMap, loaded: promoMessageMapLoaded } = usePromoMessageMap();
    const promoHandle = extractPromoHandle(product.tags);
    const promoEntry = promoHandle ? promoMessageMap.get(promoHandle) : null;
    const promoMessage = (_a = promoEntry === null || promoEntry === void 0 ? void 0 : promoEntry.displayText) !== null && _a !== void 0 ? _a : null;
    // Reserve layout for the label while the map is still fetching — prevents CLS
    // when a tagged product card mounts before the Storefront response resolves.
    const showPromoSlot = Boolean(promoMessage) || (Boolean(promoHandle) && !promoMessageMapLoaded);
    const promoStyle = showPromoSlot
        ? {
            color: (promoEntry === null || promoEntry === void 0 ? void 0 : promoEntry.textColor) || undefined,
            backgroundColor: (promoEntry === null || promoEntry === void 0 ? void 0 : promoEntry.backgroundColor) || undefined,
            visibility: (promoMessage ? undefined : 'hidden'),
        }
        : undefined;
    const isImageObj = (img) => typeof img === 'object' && 'url' in img;
    // Get variants from customAttributes (safely)
    const variants = (customAttributes && customAttributes.boostVariants) ? customAttributes.boostVariants : [];
    // Extract unique colors from variants.
    // TODO: Aquila currently ships one colour variant per product — the swatch hex
    // comes from the product-level `custom.hex_colour` / `custom.base_colour`
    // metafields (see `variantSwatchHex` below), so every entry in this list
    // renders the same colour. Revisit this if/when Aquila starts selling products
    // with multiple colour variants in a single product record (we'll need to
    // resolve the hex per-variant instead of per-product).
    const colorVariants = useMemo(() => {
        if (!Array.isArray(variants) || variants.length === 0)
            return [];
        const colorMap = new Map();
        try {
            variants.forEach((variant) => {
                const color = getVariantColor(variant);
                if (color && !colorMap.has(color)) {
                    colorMap.set(color, {
                        color,
                        image: variant.image || null,
                        available: variant.available
                    });
                }
            });
        }
        catch (e) {
            console.error('Error processing variants:', e);
        }
        return Array.from(colorMap.values());
    }, [variants]);
    const [selectedColor, setSelectedColor] = useState(colorVariants.length > 0 ? colorVariants[0].color : null);
    /** Storefront Product GID for metafield `related_colourways` swatch selection */
    const [selectedRelatedColourwayGid, setSelectedRelatedColourwayGid] = useState(null);
    const [showQuickShop, setShowQuickShop] = useState(false);
    const videoRef = useRef(null);
    const isHoveredRef = useRef(false);
    const [isVideoLoading, setIsVideoLoading] = useState(false);
    const [shouldLoadHoverMedia, setShouldLoadHoverMedia] = useState(false);
    // AQ-1087: mobile gets the swipeable image carousel; desktop keeps the
    // existing hover image-swap above. Matches the SSR-safe matchMedia pattern
    // in useColourwayStorefrontData.ts.
    const [isMobileViewport, setIsMobileViewport] = useState(() => {
        if (typeof window === 'undefined')
            return false;
        return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
    });
    useEffect(() => {
        const mql = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
        const onChange = () => setIsMobileViewport(mql.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);
    // sizeVariants for QuickShopButton: from Storefront when a related colourway is selected, else Boost variants
    const sizeVariants = useMemo(() => {
        var _a;
        if (selectedRelatedColourwayGid && colourwayStorefrontData) {
            const cw = colourwayStorefrontData.get(selectedRelatedColourwayGid);
            if ((_a = cw === null || cw === void 0 ? void 0 : cw.sizeVariants) === null || _a === void 0 ? void 0 : _a.length) {
                return cw.sizeVariants.map(sv => ({
                    size: sv.size,
                    url: cw.handle,
                    id: sv.variantId,
                    available: sv.available,
                }));
            }
            return [];
        }
        if (!Array.isArray(variants) || variants.length === 0)
            return [];
        const sizeMap = new Map();
        variants.forEach((variant) => {
            if (selectedColor) {
                const color = getVariantColor(variant);
                if (!color || (color || '').toLowerCase() !== (selectedColor || '').toLowerCase())
                    return;
            }
            if (Array.isArray(variant.merged_options)) {
                variant.merged_options.forEach((option) => {
                    if (option.startsWith('size:')) {
                        const size = option.replace('size:', '').trim();
                        if (size && !sizeMap.has(size)) {
                            sizeMap.set(size, {
                                size,
                                url: variant.url || variant.productUrl || variant.handle || product.handle || undefined,
                                id: variant.id,
                                available: variant.available !== false,
                            });
                        }
                    }
                });
            }
        });
        return Array.from(sizeMap.values());
    }, [selectedRelatedColourwayGid, colourwayStorefrontData, variants, product.handle, selectedColor]);
    // Get current image based on selected color
    const getCurrentImage = () => {
        if (selectedColor && colorVariants.length > 0) {
            const colorVariant = colorVariants.find(cv => cv.color === selectedColor);
            if (colorVariant && colorVariant.image) {
                return toImageUrl(colorVariant.image);
            }
        }
        // Fallback to default product image
        return toImageUrl(image);
    };
    const colourSwatches = useMemo(() => parseRelatedColourwaysMetafield(product), [product.metafields]);
    const activeColourway = selectedRelatedColourwayGid && colourwayStorefrontData
        ? colourwayStorefrontData.get(selectedRelatedColourwayGid)
        : undefined;
    const baseImgUrl = getCurrentImage();
    const baseImgAlt = isImageObj(image) ? image.alt : title;
    const baseSecondImgUrl = toImageUrl(images === null || images === void 0 ? void 0 : images[1]) || baseImgUrl;
    const displayImgUrl = (_e = (_c = (_b = activeColourway === null || activeColourway === void 0 ? void 0 : activeColourway.images[0]) === null || _b === void 0 ? void 0 : _b.url) !== null && _c !== void 0 ? _c : (selectedRelatedColourwayGid
        ? (_d = colourSwatches.find(s => normalizeShopifyProductGid(s.id) === selectedRelatedColourwayGid)) === null || _d === void 0 ? void 0 : _d.image
        : undefined)) !== null && _e !== void 0 ? _e : baseImgUrl;
    const displaySecondImgUrl = (_j = (_g = (_f = activeColourway === null || activeColourway === void 0 ? void 0 : activeColourway.images[1]) === null || _f === void 0 ? void 0 : _f.url) !== null && _g !== void 0 ? _g : (_h = activeColourway === null || activeColourway === void 0 ? void 0 : activeColourway.images[0]) === null || _h === void 0 ? void 0 : _h.url) !== null && _j !== void 0 ? _j : baseSecondImgUrl;
    const isSecondMediaVideo = isVideoUrl(displaySecondImgUrl);
    const secondMediaItem = images === null || images === void 0 ? void 0 : images[1];
    const secondMediaPoster = isSecondMediaVideo
        ? (_l = (_k = activeColourway === null || activeColourway === void 0 ? void 0 : activeColourway.images[1]) === null || _k === void 0 ? void 0 : _k.video_thumbnail) !== null && _l !== void 0 ? _l : (secondMediaItem && typeof secondMediaItem !== 'string' ? secondMediaItem.video_thumbnail : undefined)
        : undefined;
    // Resume playback when the video src changes while the card is still hovered
    // (e.g. selecting a swatch swaps the colourway's video). Without this, the
    // browser pauses and resets on every src change.
    useEffect(() => {
        if (!isHoveredRef.current || !shouldLoadHoverMedia || !isSecondMediaVideo || !videoRef.current)
            return;
        setIsVideoLoading(true);
        videoRef.current.play().catch(() => setIsVideoLoading(false));
    }, [displaySecondImgUrl, isSecondMediaVideo, shouldLoadHoverMedia]);
    const displayAlt = (_q = (_p = (_o = (_m = activeColourway === null || activeColourway === void 0 ? void 0 : activeColourway.images[0]) === null || _m === void 0 ? void 0 : _m.altText) !== null && _o !== void 0 ? _o : baseImgAlt) !== null && _p !== void 0 ? _p : activeColourway === null || activeColourway === void 0 ? void 0 : activeColourway.title) !== null && _q !== void 0 ? _q : title;
    const displayTitle = (_r = activeColourway === null || activeColourway === void 0 ? void 0 : activeColourway.title) !== null && _r !== void 0 ? _r : title;
    const productHref = `/products/${(_s = activeColourway === null || activeColourway === void 0 ? void 0 : activeColourway.handle) !== null && _s !== void 0 ? _s : product.handle}`;
    const primaryImageSrc = getShopifyImageUrl(displayImgUrl, 480);
    const primaryImageSrcSet = getShopifyImageSrcSet(displayImgUrl, CARD_IMAGE_WIDTHS);
    const hoverImageSrc = getShopifyImageUrl(displaySecondImgUrl, 480);
    const hoverImageSrcSet = getShopifyImageSrcSet(displaySecondImgUrl, CARD_IMAGE_WIDTHS);
    const shouldRenderHoverMedia = shouldLoadHoverMedia && displaySecondImgUrl !== displayImgUrl;
    // AQ-1087: full media set for the mobile carousel (every available slide,
    // not just the first two). Only rendered when isMobileViewport — desktop
    // keeps rendering displayImgUrl/displaySecondImgUrl above — but it's cheap
    // (memoised) and keeping it unconditional avoids a conditional hook.
    const mediaItems = useMemo(() => {
        var _a, _b;
        // 1) Active related colourway — use its Storefront media list directly.
        if ((_a = activeColourway === null || activeColourway === void 0 ? void 0 : activeColourway.images) === null || _a === void 0 ? void 0 : _a.length) {
            return activeColourway.images.map(m => {
                var _a, _b;
                return ({
                    url: isVideoUrl(m.url) ? m.url : ((_a = getShopifyImageUrl(m.url, 480)) !== null && _a !== void 0 ? _a : m.url),
                    alt: (_b = m.altText) !== null && _b !== void 0 ? _b : displayAlt,
                    isVideo: isVideoUrl(m.url),
                    poster: m.video_thumbnail,
                    srcSet: isVideoUrl(m.url) ? undefined : getShopifyImageSrcSet(m.url, CARD_IMAGE_WIDTHS),
                    sizes: isVideoUrl(m.url) ? undefined : CARD_IMAGE_SIZES,
                });
            });
        }
        // 2) Related colourway swatch selected but Storefront data not loaded yet —
        // show just the swatch's single image until the media list resolves.
        if (selectedRelatedColourwayGid) {
            const swatchImg = (_b = colourSwatches.find(s => normalizeShopifyProductGid(s.id) === selectedRelatedColourwayGid)) === null || _b === void 0 ? void 0 : _b.image;
            if (swatchImg) {
                return [toMediaItem(swatchImg, displayAlt)].filter(Boolean);
            }
        }
        // 3) Base product media. The first slide honours the selected Boost colour
        // variant image (displayImgUrl); the rest come from the product images array.
        const items = [];
        const seen = new Set();
        const push = (item) => {
            if (item && !seen.has(item.url)) {
                seen.add(item.url);
                items.push(item);
            }
        };
        const imageItems = (images !== null && images !== void 0 ? images : [])
            .map(img => toMediaItem(img, displayAlt))
            .filter(Boolean);
        if (displayImgUrl && toImageUrl(images === null || images === void 0 ? void 0 : images[0]) !== displayImgUrl) {
            push(toMediaItem(displayImgUrl, displayAlt));
        }
        imageItems.forEach(push);
        if (items.length === 0 && baseImgUrl) {
            push(toMediaItem(baseImgUrl, displayAlt));
        }
        return items;
    }, [activeColourway, selectedRelatedColourwayGid, colourSwatches, displayImgUrl, baseImgUrl, images, displayAlt]);
    const hasSpecial = price && 'special' in price && price.special !== undefined && price.special < ((_u = ((_t = price.sale) !== null && _t !== void 0 ? _t : price.regular)) !== null && _u !== void 0 ? _u : price.regular);
    const isOnSale = price && price.sale !== undefined && price.sale < price.regular;
    // Calculate discount percentage. Guards against null/undefined/zero inputs that
    // would otherwise produce NaN or ±Infinity (e.g. missing regular, sale = 0).
    const getDiscountPercentage = () => {
        if (hasSpecial && price.special && price.special > 0) {
            const original = price.sale && price.sale > 0 ? price.sale : price.regular;
            if (!original || original <= 0 || original <= price.special)
                return 0;
            return Math.round(((original - price.special) / original) * 100);
        }
        if (isOnSale && price.sale && price.sale > 0 && price.regular > 0) {
            return Math.round(((price.regular - price.sale) / price.regular) * 100);
        }
        return 0;
    };
    const discountPercent = getDiscountPercentage();
    // Get matching badges from theme settings
    const badgeValue = useMemo(() => { var _a, _b; return ((_b = (_a = product.metafields) === null || _a === void 0 ? void 0 : _a.find((mf) => mf.namespace === 'custom' && mf.key === 'badge' && mf.value)) === null || _b === void 0 ? void 0 : _b.value) || ''; }, [product.metafields]);
    // Swatch hex for this card's Boost colour variants — single source per product.
    // Prefer the explicit `custom.hex_colour` metafield; if that's missing/null, fall
    // back to mapping the `custom.base_colour` name through `getColourVariable` (same
    // lookup table the PLP uses elsewhere for name→hex defaults).
    const variantSwatchHex = useMemo(() => {
        var _a, _b;
        const hexColour = (_a = metafields === null || metafields === void 0 ? void 0 : metafields.find((mf) => mf.namespace === 'custom' && mf.key === 'hex_colour')) === null || _a === void 0 ? void 0 : _a.value;
        if (hexColour && hexColour.trim())
            return hexColour;
        const baseColour = (_b = metafields === null || metafields === void 0 ? void 0 : metafields.find((mf) => mf.namespace === 'custom' && mf.key === 'base_colour')) === null || _b === void 0 ? void 0 : _b.value;
        return getColourVariable((baseColour || '').toLowerCase());
    }, [metafields]);
    // Optional promotional copy set directly on the product's `custom.promotional_text`
    // metafield — shown after the colour swatches when present, distinct from the
    // tag-driven `plp_promo_message` metaobject system above.
    const promotionalText = useMemo(() => { var _a, _b; return ((_b = (_a = product.metafields) === null || _a === void 0 ? void 0 : _a.find((mf) => mf.namespace === 'custom' && mf.key === 'promotional_text' && mf.value)) === null || _b === void 0 ? void 0 : _b.value) || ''; }, [product.metafields]);
    const matchedBadges = useMemo(() => {
        var _a, _b;
        const config = window.mrPLPConfig;
        if (!((_b = (_a = config === null || config === void 0 ? void 0 : config.badgeConfig) === null || _a === void 0 ? void 0 : _a.badges) === null || _b === void 0 ? void 0 : _b.length) || !badgeValue)
            return [];
        return config.badgeConfig.badges.filter((badge) => (badgeValue || '').toLowerCase() === (badge.tag || '').toLowerCase());
    }, [badgeValue]);
    const hasUnmatchedBadge = badgeValue && matchedBadges.length === 0;
    const isUppercase = ((_w = (_v = window.mrPLPConfig) === null || _v === void 0 ? void 0 : _v.badgeConfig) === null || _w === void 0 ? void 0 : _w.uppercase) === 'uppercase';
    const handleVariantColorClick = (event, color) => {
        event.preventDefault();
        // Swatch clicks live inside the card's <a>; stop the bubble so they don't
        // register as a product click (trackSelectItem) on the anchor.
        event.stopPropagation();
        setSelectedRelatedColourwayGid(null);
        setSelectedColor(color);
    };
    // Fire Boost's product-click (`view_product`) when the card is activated.
    // No-op when no TrackingProvider is mounted (e.g. tests, or a surface that
    // hasn't opted into tracking yet) — a missing context must never crash a render.
    const handleCardClick = () => {
        if (!tracking)
            return;
        analytics.trackSelectItem({
            itemId: String(product.id),
            itemListId: tracking.itemListId,
            searchTerm: tracking.searchTerm,
            pageType: tracking.pageType,
            correlationId: tracking.correlationId,
            widgetId: tracking.widgetId,
        });
    };
    // Fire Boost's `add_to_cart` when a size is added from this card's QuickShop.
    // No-op without a TrackingProvider. The provider resolves the cart token via
    // `/cart.js` (the add has completed by now) — see `trackAddToCart`.
    const handleAddedToCart = (variantId) => {
        if (!tracking)
            return;
        analytics.trackAddToCart({
            itemId: String(product.id),
            itemVariantId: String(variantId),
            itemListId: tracking.itemListId,
            searchTerm: tracking.searchTerm,
            pageType: tracking.pageType,
            correlationId: tracking.correlationId,
            widgetId: tracking.widgetId,
        });
    };
    const handleRelatedColourwayClick = (event, colourSwatch) => {
        event.preventDefault();
        event.stopPropagation();
        const gid = normalizeShopifyProductGid(colourSwatch.id);
        if (gid)
            setSelectedRelatedColourwayGid(gid);
    };
    return (h(Fragment, null,
        h("a", { href: productHref, onClick: handleCardClick, class: layout === 'gridTwo' || layout === 'gridFour' ?
                styles.productCard :
                styles.productCardList, onMouseEnter: () => {
                isHoveredRef.current = true;
                setShouldLoadHoverMedia(true);
            }, onMouseLeave: () => {
                isHoveredRef.current = false;
                if (videoRef.current) {
                    videoRef.current.pause();
                    videoRef.current.currentTime = 0;
                }
                setIsVideoLoading(false);
            }, onTouchStart: () => {
                // Touch browsers apply the CSS `:hover` state as soon as the finger
                // makes contact (that's what the `.productImage:hover` crossfade in
                // ProductCard.module.scss is keying off), but they don't fire a
                // synthetic `mouseenter` until `touchend` — by which point the tap
                // has already triggered navigation to the PDP. Gating hover-media
                // mounting on `mouseenter` alone (as this used to) left the second
                // image/video unmounted for the entire press-and-hold, so the CSS
                // faded in nothing and the tile went blank on mobile (AQ-1092).
                // Mount it on `touchstart` instead so it's already in the DOM the
                // moment CSS starts showing it.
                isHoveredRef.current = true;
                setShouldLoadHoverMedia(true);
            } },
            h("div", { class: styles.productImage },
                isMobileViewport ? (
                // AQ-1087: mobile swipes through the full media set instead of the
                // desktop hover-swap below.
                h(ProductCardCarousel, { items: mediaItems, imagePriority: imagePriority })) : (h(Fragment, null,
                    h("img", { src: primaryImageSrc, srcSet: primaryImageSrcSet, sizes: CARD_IMAGE_SIZES, alt: displayAlt, loading: imagePriority ? 'eager' : 'lazy', fetchPriority: imagePriority ? 'high' : 'auto' }),
                    shouldRenderHoverMedia && isSecondMediaVideo ? (h(Fragment, null,
                        h("video", { ref: videoRef, src: displaySecondImgUrl, poster: secondMediaPoster, muted: true, playsInline: true, loop: true, preload: "none", "aria-hidden": "true", class: styles.hoverMedia, onPlaying: () => setIsVideoLoading(false), onWaiting: () => setIsVideoLoading(true), onStalled: () => setIsVideoLoading(true) }),
                        isVideoLoading && h("div", { class: styles.hoverMediaLoader, "aria-hidden": "true" }))) : shouldRenderHoverMedia ? (h("img", { src: hoverImageSrc, srcSet: hoverImageSrcSet, sizes: CARD_IMAGE_SIZES, alt: displayAlt })) : null)),
                (availability === 'out_of_stock' || matchedBadges.length > 0 || hasUnmatchedBadge) && (h("div", { class: styles.badgeContainer },
                    availability === 'out_of_stock' && (h("span", { class: styles.badge, style: "background-color: var(--color-sold-out-background, #000); color: var(--color-sold-out-text, #fff);" }, isUppercase ? 'SOLD OUT' : 'Sold Out')),
                    matchedBadges.map((badge) => (h("span", { key: badge.tag, class: styles.badge, style: `background-color: ${badge.backgroundColor}; color: ${badge.textColor};` }, isUppercase ? badge.label.toUpperCase() : badge.label))),
                    hasUnmatchedBadge && (h("span", { class: styles.badge, style: "background: transparent; color: #474747; text-transform: uppercase;" }, badgeValue.toUpperCase())))),
                h(QuickShopButton, { sizeVariants: sizeVariants, onAddedToCart: handleAddedToCart, onClick: () => setShowQuickShop(true) })),
            h("div", { class: styles.productInfo },
                h("span", { class: styles.productTitle }, displayTitle),
                h("div", { class: styles.productPrice }, activeColourway ? (() => {
                    const colourwayPrice = formatColourwayPrice(activeColourway);
                    return colourwayPrice.onSale ? (h(Fragment, null,
                        h("span", { class: styles.salePrice }, colourwayPrice.sale),
                        h("span", { class: styles.regularPrice }, colourwayPrice.regular),
                        colourwayPrice.discount > 0 && h("span", { class: styles.discountBadge },
                            "-",
                            colourwayPrice.discount,
                            "% OFF"))) : (h("span", null, colourwayPrice.sale));
                })() : hasSpecial ? (h(Fragment, null,
                    h("span", { class: styles.salePrice },
                        "$",
                        price.special),
                    h("span", { class: styles.regularPrice },
                        "$", (_x = price.sale) !== null && _x !== void 0 ? _x : price.regular),
                    discountPercent > 0 && h("span", { class: styles.discountBadge },
                        "-",
                        discountPercent,
                        "%"))) : isOnSale ? (h(Fragment, null,
                    h("span", { class: styles.salePrice },
                        "$",
                        price.sale),
                    h("span", { class: styles.regularPrice },
                        "$",
                        price.regular),
                    discountPercent > 0 && h("span", { class: styles.discountBadge },
                        "-",
                        discountPercent,
                        "% OFF"))) : (h("span", null,
                    "$",
                    price.regular))),
                h("div", { class: styles.swatchContainer },
                    colorVariants.length > 0 && (h(Fragment, null, colorVariants.map((colorVariant) => (h("div", { key: colorVariant.color, class: `${styles.swatch} ${!selectedRelatedColourwayGid && selectedColor === colorVariant.color ? styles.swatchSelected : styles.swatchUnselected}`, onClick: (event) => handleVariantColorClick(event, colorVariant.color) },
                        h("span", { class: styles.swatchInner, style: `background-color: ${variantSwatchHex};`, title: colorVariant.color })))))),
                    colourSwatches.length > 0 && (h(Fragment, null, colourSwatches.map((colourSwatch) => {
                        var _a, _b, _c;
                        const swatchName = (_a = colourSwatch.name) !== null && _a !== void 0 ? _a : '';
                        const swatchGid = normalizeShopifyProductGid(colourSwatch.id);
                        const bgHex = (colourSwatch.styleColourHex && colourSwatch.styleColourHex.trim()) ||
                            getColourVariable(((_b = colourSwatch === null || colourSwatch === void 0 ? void 0 : colourSwatch.baseColour) === null || _b === void 0 ? void 0 : _b.toLowerCase()) || 'grey');
                        return (h("div", { key: String((_c = colourSwatch.id) !== null && _c !== void 0 ? _c : swatchName), class: `${styles.swatch} ${swatchGid && selectedRelatedColourwayGid === swatchGid ? styles.swatchSelected : styles.swatchUnselected}`, onClick: (event) => handleRelatedColourwayClick(event, colourSwatch) },
                            h("span", { class: styles.swatchInner, style: `background-color: ${bgHex};`, title: `${swatchName} - ${colourSwatch === null || colourSwatch === void 0 ? void 0 : colourSwatch.styleColour}` })));
                    })))),
                promotionalText && (h("div", { class: styles.promotionalText }, promotionalText)),
                showPromoSlot && (h("div", { class: styles.promoMessage, style: promoStyle }, promoMessage || '\u00A0')))),
        showQuickShop && (customAttributes === null || customAttributes === void 0 ? void 0 : customAttributes.boostRawProduct) && (h(QuickShopModal, { product: customAttributes.boostRawProduct, onClose: () => setShowQuickShop(false), selectedColor: selectedColor }))));
}
